/**
 * Identity-inlining of group I/O pass-through nodes (`groupInput` /
 * `groupOutput`) — the REFERENCE IMPLEMENTATION of the engine contract in
 * docs/superpowers/specs/2026-06-11-group-io-passthrough-design.md.
 *
 * A pass-through forwards its single upstream source to all consumers:
 * every outgoing edge is rewritten to originate from the pass-through's
 * upstream (source, sourceHandle); the pass-through and its edges are
 * dropped. Chains (nested groups) resolve by iterating to a fixed point.
 * Unconnected pass-throughs simply drop their outgoing edges — their
 * consumers are unconnected, which is a valid graph state.
 *
 * Used by: dissolve-group (restore direct edges) and as a pre-pass before
 * any serializer that needs a flat graph (the EdgeScript printer, for one).
 * The backtesting engine implements the same algorithm server-side.
 */

const PASSTHROUGH_TYPES = new Set(['subnetInput', 'subnetOutput', 'reroute'])

export const isPassthroughType = (type: string | undefined): boolean =>
  !!type && PASSTHROUGH_TYPES.has(type)

interface MinimalNode {
  id: string
  type?: string
  data?: unknown
}

interface MinimalEdge {
  id: string
  source: string
  target: string
  sourceHandle?: string | null
  targetHandle?: string | null
  data?: unknown
}

/** One real input a pass-through feeds: the node, its socket, and what the wire rule reads. */
export interface PassthroughConsumer {
  nodeId: string
  handle: string
  type: string
  data: unknown
}

/**
 * The real (non-pass-through) inputs a pass-through feeds, walking chains
 * downstream: through reroutes and nested subnet pills, and through a
 * subnetOutput pill to what the subnet feeds outside. The mirror of
 * resolvePassthroughUpstream: what an unfed subnet input needs is what these
 * take (subnet-input-needs.ts), and a wire into a pass-through is checked
 * against every one of them. A collapsed card's display wires (`data.proxy`,
 * collapse-model's isProxyEdge, not imported: collapse-model depends on this
 * file) stand for these canonical ones, so they are skipped.
 */
export function resolvePassthroughDownstream(
  passthroughId: string,
  nodes: MinimalNode[],
  edges: MinimalEdge[],
): PassthroughConsumer[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const consumers: PassthroughConsumer[] = []
  const found = new Set<string>()
  const seen = new Set<string>([passthroughId])
  const queue = [passthroughId]
  while (queue.length > 0) {
    const currentId = queue.shift()!
    for (const edge of edges) {
      if (edge.source !== currentId || (edge.data as { proxy?: unknown })?.proxy === true) continue
      const target = byId.get(edge.target)
      if (!target) continue
      if (isPassthroughType(target.type)) {
        if (!seen.has(target.id)) {
          seen.add(target.id)
          queue.push(target.id)
        }
        continue
      }
      const handle = edge.targetHandle ?? ''
      const key = `${target.id}:${handle}`
      if (found.has(key)) continue
      found.add(key)
      consumers.push({ nodeId: target.id, handle, type: target.type ?? '', data: target.data })
    }
  }
  return consumers
}

/**
 * Resolve the real (non-pass-through) node feeding a pass-through, walking
 * chains. Null when unconnected. Used for pass-through-aware connection
 * validation: a wire FROM a pass-through validates as if it came from the
 * effective upstream source.
 */
export function resolvePassthroughUpstream(
  passthroughId: string,
  nodes: MinimalNode[],
  edges: MinimalEdge[],
): { id: string; type: string; sourceHandle: string | null | undefined } | null {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const seen = new Set<string>()
  let currentId = passthroughId
  for (;;) {
    if (seen.has(currentId)) return null // defensive: cycle
    seen.add(currentId)
    const incoming = edges.find((e) => e.target === currentId)
    if (!incoming) return null
    const sourceNode = byId.get(incoming.source)
    if (!sourceNode) return null
    if (!isPassthroughType(sourceNode.type)) {
      return { id: sourceNode.id, type: sourceNode.type ?? '', sourceHandle: incoming.sourceHandle }
    }
    currentId = sourceNode.id
  }
}

/**
 * What a wire FROM `source` validates as: a real node as itself; a
 * pass-through (subnet pill, reroute) as the real node feeding it, walking
 * chains; null when a pass-through has nothing upstream yet, so the caller
 * skips validation until its outside is wired. Both connect paths (a collapsed
 * subnet's boundary and a plain drop) ask this, so wiring a subnet's output
 * into a typed input validates the same collapsed or expanded.
 */
export function resolveValidationSource(
  source: MinimalNode,
  sourceHandle: string | null | undefined,
  nodes: MinimalNode[],
  edges: MinimalEdge[],
): { type: string; handle: string } | null {
  if (!isPassthroughType(source.type)) {
    return { type: source.type ?? '', handle: sourceHandle || 'output' }
  }
  const upstream = resolvePassthroughUpstream(source.id, nodes, edges)
  return upstream ? { type: upstream.type, handle: upstream.sourceHandle ?? 'output' } : null
}

export function inlinePassthroughs<N extends MinimalNode, E extends MinimalEdge>(
  nodes: N[],
  edges: E[],
  /** Restrict inlining (e.g. only one group's pills on dissolve). Defaults to all pass-throughs. */
  shouldInline: (node: N) => boolean = (node) => isPassthroughType(node.type),
): { nodes: N[]; edges: E[] } {
  const passthroughIds = new Set(
    nodes.filter((n) => isPassthroughType(n.type) && shouldInline(n)).map((n) => n.id),
  )
  if (passthroughIds.size === 0) return { nodes, edges }

  // Resolve the effective upstream (source, sourceHandle) of a pass-through,
  // following chains. Returns null when the chain dead-ends unconnected.
  const incomingByTarget = new Map<string, E>()
  for (const edge of edges) {
    if (passthroughIds.has(edge.target)) incomingByTarget.set(edge.target, edge)
  }

  const resolveUpstream = (
    passthroughId: string,
    seen: Set<string>,
  ): { source: string; sourceHandle: string | null | undefined } | null => {
    if (seen.has(passthroughId)) return null // defensive: cycle
    seen.add(passthroughId)
    const incoming = incomingByTarget.get(passthroughId)
    if (!incoming) return null
    if (passthroughIds.has(incoming.source)) return resolveUpstream(incoming.source, seen)
    return { source: incoming.source, sourceHandle: incoming.sourceHandle }
  }

  const nextNodes = nodes.filter((n) => !passthroughIds.has(n.id))
  const nextEdges: E[] = []
  for (const edge of edges) {
    // Edges INTO a pass-through disappear (consumed by resolution).
    if (passthroughIds.has(edge.target)) continue
    if (!passthroughIds.has(edge.source)) {
      nextEdges.push(edge)
      continue
    }
    const upstream = resolveUpstream(edge.source, new Set())
    if (!upstream) continue // unconnected pass-through → consumer stays unwired
    nextEdges.push({
      ...edge,
      id: `inlined:${upstream.source}:${upstream.sourceHandle ?? ''}->${edge.target}:${edge.targetHandle ?? ''}`,
      source: upstream.source,
      sourceHandle: upstream.sourceHandle,
    })
  }

  return { nodes: nextNodes, edges: nextEdges }
}
